import { Box, Grid, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import { integrationKeys } from "../../../../api/queryKeys";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import CustomCopyButton from "../../../../components/CustomCopyButton";
import LoadingIcon from "../../../../components/LoadingIcon";
import { Toast } from "../../../../components/Toast";
import { logos } from "../../environment/icons/iconExport";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import Guideline from "../components/Guideline";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import ClaudeConnectionForm from "./ClaudeConnectionForm";
import CLAUDE_1 from "./images/CLAUDE_1.png";
import CLAUDE_2 from "./images/CLAUDE_2.png";

const ClaudeIntegration = () => {
  const [searchParams] = useSearchParams();
  const [guideMode, setGuideMode] = useState(false);
  const { isIntegrated, isIntegratedLoading } = useIsIntegrated("claude");
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();
  const { registerClaudeIntegration } = useAPI();
  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();

  useEffect(() => {
    if (searchParams.has("viewonly")) {
      setGuideMode(true);
    }
  }, [searchParams]);

  const onSubmit = (values) => {
    setLoading(true);
    registerClaudeIntegration(values.admin_api_key.trim())
      .then(() => {
        queryClient.invalidateQueries({
          queryKey: integrationKeys.all,
        });
        Toast.success("Claude integration successful");
      })
      .catch((error) => {
        if (error.response) {
          Toast.error(`Something went wrong: ${error.response.data.detail}`);
        } else {
          Toast.error("An error occurred while processing the request.");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Box>
      <Guideline
        title={
          !isIntegrated
            ? "Once integrated, the Breeze platform will automatically read the configurations from Claude and prioritize the suggested remediation."
            : ""
        }
        image={logos.ClaudeLogo}
        iconBorderRadius={0}
        name={"Claude"}
        content={
          loading || isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <Box>
              <Typography variant={"h4"}>Claude is integrated!</Typography>
            </Box>
          ) : (
            <div>
              <Box mb="20px">
                <Grid container spacing={2} direction={"row"}>
                  {guideMode && <IntegrationGuideAlert />}
                </Grid>
              </Box>
              <Box mb="20px">
                <Grid container spacing={2} direction={"row"}>
                  <Grid item>
                    <SecondaryButton onClick={handleExpandAll}>
                      Expand All Screenshots
                    </SecondaryButton>
                  </Grid>
                  <Grid item>
                    <SecondaryButton onClick={handleCollapseAll}>
                      Collapse All Screenshots
                    </SecondaryButton>
                  </Grid>
                </Grid>
              </Box>
              <GuidelineStepByStep>
                <GuidelineStep title="Navigate to the Admin API console in Claude">
                  <Box
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                    }}
                  >
                    <Typography variant="body1">
                      Navigate to your Claude Admin Settings to generate an
                      Admin API Key.
                    </Typography>
                    <PrimaryButton
                      onClick={() => {
                        window.open(
                          "https://platform.claude.com/settings/admin-keys",
                          "_blank"
                        );
                      }}
                      style={{ textTransform: "none", width: "fit-content" }}
                    >
                      Open Claude Console Settings
                    </PrimaryButton>
                    <Typography variant="body1">
                      In the MANAGE, Click on "API Keys" and then "Admin Keys"
                    </Typography>
                    <ScreenshotAccordion
                      images={[
                        {
                          file: CLAUDE_1,
                          altext: "Claude Admin API Key Creation",
                        },
                      ]}
                      onExpanded={addToExpandedList}
                    />
                  </Box>
                </GuidelineStep>
                <GuidelineStep title="Create an Admin API Key">
                  <Box
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                    }}
                  >
                    <Typography variant="body1">
                      1. Click on "Create Admin Key" and provide a name for the
                      token (e.g., <strong>Breeze Security Platform</strong>
                      <Box
                        component="span"
                        sx={{
                          display: "inline-flex",
                          verticalAlign: "middle",
                          ml: 0.5,
                        }}
                      >
                        <CustomCopyButton
                          copyTitle=""
                          copyText="Breeze Security Platform"
                        />
                      </Box>
                      )
                    </Typography>
                    <ScreenshotAccordion
                      images={[
                        {
                          file: CLAUDE_2,
                          altext: "Claude Admin API Key Creation",
                        },
                      ]}
                      onExpanded={addToExpandedList}
                    />
                    <Typography>
                      2. Click "Add"
                      <br />
                      3. Copy the generated token (it will not be displayed
                      again)
                      <br />
                      4. Paste the token below.
                    </Typography>
                  </Box>
                </GuidelineStep>
                <GuidelineStep id="connectClaude" title="Connect Claude">
                  <Box width={700}>
                    <ClaudeConnectionForm
                      onSubmit={onSubmit}
                      guideMode={guideMode}
                    />
                  </Box>
                </GuidelineStep>
              </GuidelineStepByStep>
            </div>
          )
        }
      />
    </Box>
  );
};

export default ClaudeIntegration;
